import { ACTToolbarService } from 'src/app/angularJS/services/toolbar.service';
import { BreadcrumbService } from 'src/app/angularJS/services/breadcrumbs/breadcrumb.service';
import { JourneyConstructionRuleService } from 'src/app/angularJS/services/journey-construction-rule/journey-construction-rule.service';
import { QueryBuilder } from 'src/app/angularJS/services/query-builder.service';
import { UserMessagesService } from 'src/app/angularJS/services/user-messages.service';
import { LinkBuilder } from 'src/app/angularJS/services/link-builder.service';
import { UserService } from 'src/app/angularJS/services/user/user.service';

export class JourneyConstructionRulesController {
  public static $inject = [
    'ACTToolbarService',
    'breadcrumbService',
    'journeyConstructionRuleService',
    'QueryBuilder',
    'PrimaryAction',
    '$state',
    'UserMessages',
    'LinkBuilder',
    'userService'
  ];
  public transportOperatorDictionary: InterACT.Interfaces.IDictionary<InterACT.Interfaces.TransportOperators.TransportOperator> = {};
  public journeyConstructionRules: Array<InterACT.Interfaces.JourneyConstructionRules.JourneyConstructionRule> = [];
  public filters = {
    type: 'All',
    validFrom: '',
    validTo: ''
  };
  public paginatorData: InterACT.Interfaces.IPaginationData = {
    currentPage: 1,
    perPageCount: 15,
    totalCount: 0
  };

  constructor(
    private toolbarService: ACTToolbarService,
    private breadcrumbService: BreadcrumbService,
    private journeyConstructionRuleService: JourneyConstructionRuleService,
    private queryBuilder: QueryBuilder,
    private PrimaryAction: any,
    private $state: ng.ui.IStateService,
    private userMessagesService: UserMessagesService,
    private linkBuilder: LinkBuilder,
    private userService: UserService) {
    this.activate();
  }

  /**
   * A method that is called to retrieve a page of data
   * @param page the page number to retrieve
   * @param perPage the number of records to retrieve per page
   */
  public getData(page: number, perPage: number) {
    const qParam = this.constructFilterQuery();

    if (!page) {
      page = this.paginatorData.currentPage;
    }

    if (!perPage) {
      perPage = this.paginatorData.perPageCount;
    }

    this.journeyConstructionRuleService.getList(page, perPage, qParam).then((response) => {
      this.paginatorData.totalCount = response.meta.totalCount;

      const operatorIds = [];
      this.journeyConstructionRules = response.journeyConstructionRules.map((ruleWithMeta: any) => {
        if (ruleWithMeta.journeyConstructionRule.operator && ruleWithMeta.journeyConstructionRule.operator.link) {
          const operatorId = ruleWithMeta.journeyConstructionRule.operator.link.split('/').pop();

          if (operatorIds.indexOf(operatorId) === -1 && !this.transportOperatorDictionary[operatorId]) {
            operatorIds.push(operatorId);
          }
        }

        return ruleWithMeta.journeyConstructionRule;
      });
    });
  }

  /**
   * A method that handles the event where a filter has been changed on the UI.
   */
  public onFilterChanged() {
    this.getData(this.paginatorData.currentPage, this.paginatorData.perPageCount);
  }

  /**
   * A method that takes an enum version of a rule type and returns the corresponding name for it
   * @param enumName the enum string to get the type name for
   */
  public getRuleType(enumName: string) {
    switch (enumName) {
      case 'SAME_LOCATION_EXIT_TIME':
        return 'SLET';
      case 'MAXIMUM_JOURNEY_TIME':
        return 'MJT';
      case 'USAGE_VALIDITY_PERIOD':
        return 'UVP';
      case 'DOUBLE_TAP':
        return 'DCT';
      case 'SAME_DEVICE_TRIP':
        return 'SDT';
      case 'BREAK_OF_JOURNEY':
        return 'BOJ';
      case 'REPLACEMENT_VEHICLE':
        return 'RV'
      default:
        return 'NA';
    }
  }

  /**
   * Function to navigates to the journey-construction-rules-create state
   */
  public createRule = () => {
    this.$state.go('journey-construction-rule-manage');
  }

  /**
   * Function to navigates to the journey-construction-rules-create state
   */
  public editRule = (journeyConstructionRule: InterACT.Interfaces.JourneyConstructionRules.JourneyConstructionRule) => {
    this.$state.go('journey-construction-rule-manage', { id: journeyConstructionRule.id });
  }

  /**
   * A method that deletes the suplied journey construction rule
   * @param ruleId the id of the rule to delete
   */
  public deleteRule = (ruleId: string) => {
    this.userMessagesService.buildConfirmDialog(
      'DELETE_USAGE_CONSTRUCTION_RULE',
      'CONFIRM_DIALOG.USAGE_CONSTRUCTION_RULES.DELETE_CONFIRM',
      undefined,
      'CONFIRM_DIALOG.USAGE_CONSTRUCTION_RULES.CONFIRM',
      'CONFIRM_DIALOG.USAGE_CONSTRUCTION_RULES.CANCEL'
    ).then(() => {
      this.journeyConstructionRuleService.delete(ruleId).then(() => {
        this.userMessagesService.showToast('USAGE_CONTRUCTION_RULE_DELETED');
        this.getData(this.paginatorData.currentPage, this.paginatorData.perPageCount);
      });
    });
  }

  /**
   * A method that gets the name of the transport operator associated with the supplied rule
   * @param journeyConstructionRule the rule that contains the operator to get the name of
   */
  public getOperatorName = (journeyConstructionRule: any) => {
    if (!journeyConstructionRule.operator || !journeyConstructionRule.operator.name) {
      return;
    }

    return journeyConstructionRule.operator.name;
  };

  /**
   * A method that gets the name of the transport operator service associated with the supplied rule
   * @param journeyConstructionRule the rule that contains the service to get the name of
   */
  public getServiceName = (journeyConstructionRule: any) => {
    if (!journeyConstructionRule.services || journeyConstructionRule.services.length === 0) {
      return;
    }

    return journeyConstructionRule.services[0].name;
  };

  /**
   * A method that constructs a query param to send to the service based on the filters on this controller.
   */
  private constructFilterQuery() {
    this.queryBuilder.newParam().fieldIs('scheme.link', this.linkBuilder.getSchemesLink(this.userService.selectedScheme.id))

    if (this.filters.type !== 'All') {
      this.queryBuilder.and();
      this.queryBuilder.fieldIs('type', this.filters.type);
    }

    if (this.filters.validFrom) {
      this.queryBuilder.and();
      this.queryBuilder.isGreaterOrEqualTo('validFrom', '\'' + (<any>this.filters.validFrom).toISOString() + '\'');
    }

    if (this.filters.validTo) {
      this.queryBuilder.and();
      this.queryBuilder.isLessThanOrEqualTo('validUntil', '\'' + (<any>this.filters.validTo).toISOString() + '\'');
    }

    this.queryBuilder.sortBy([{
      columnName: 'created',
      direction: 'desc'
    }]);

    return this.queryBuilder.getParam();
  }

  /**
   * A method that is called during controller instantiation
   */
  private activate() {
    this.PrimaryAction.setActions([{
      name: 'CREATE_JOURNEY_CONSTRUCTION_RULE',
      action: this.createRule
    }]);

    this.toolbarService.getToolbar('primary').then((toolbar) => {
      toolbar.setTitle('USAGE_CONSTRUCTION_RULES');
    });

    this.breadcrumbService.setBreadcrumbs([{
      name: 'ABT_CONFIGURATION',
      sref: '#'
    }, {
      name: 'USAGE_CONSTRUCTION_RULES',
      sref: 'journey-construction-rules'
    }]);

    this.getData(1, 15);
  }
}